import {
  useEffect,
  useState,
  type DependencyList,
  type RefObject,
} from 'react';

/**
 * Tracks whether a horizontally-scrolling tab strip (`.nv-tab-strip`)
 * currently overflows its container. The strip's right-edge fade mask is a
 * "scroll for more" affordance, not decoration — it must only render when
 * the strip genuinely scrolls, or it fades content that is fully visible
 * (the active tab's underline when it's the last tab, a divider border).
 * Callers toggle `.nv-tab-strip--no-fade` off the returned `fits` value.
 *
 * Re-measures on every ResizeObserver callback for the observed element, and
 * whenever an entry in `deps` changes (e.g. the tab list itself, which can
 * change the strip's natural width without resizing the container).
 *
 * Defaults to `true` (fits) so first paint and non-layout test environments
 * (jsdom/happy-dom always report `scrollWidth`/`clientWidth` as 0) render the
 * crisp, non-faded state rather than a false "overflowing" one.
 */
export function useTabStripOverflow(
  ref: RefObject<HTMLElement | null>,
  deps: DependencyList = [],
): boolean {
  const [fits, setFits] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      // 1px tolerance: sub-pixel rounding can leave scrollWidth a hair over
      // clientWidth even when nothing is actually clipped.
      setFits(el.scrollWidth <= el.clientWidth + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
    // react-hooks/exhaustive-deps is disabled repo-wide (packages/config-eslint/react.js);
    // `deps` is the caller's deliberate re-measure trigger, not an omission.
  }, deps);

  return fits;
}
